import classNames from 'classnames';
import { useRouter } from 'next/router';
import React from 'react';
import { isMobile } from 'react-device-detect';
import {
  AiFillBulb,
  AiFillHeart,
  AiOutlineBulb,
  AiOutlineHeart,
  AiOutlineMessage,
} from 'react-icons/ai';
import { FiLink2, FiShare } from 'react-icons/fi';
import { IoBookOutline, IoBookSharp } from 'react-icons/io5';
import { useAlert } from '../../../../../lib/alert';
import { useAuth } from '../../../../../lib/auth';
import { useMessages } from '../../../../../lib/messages';
import { useUser } from '../../../../../lib/user';
import MessageAction from './MessageAction';

export default function MessageActions({ mainThread, message }) {
  const user = useUser().state.user;

  const login = useAuth().login;
  const router = useRouter();
  const { messageId } = router.query;

  const freshMessage = useMessages().freshMessage;
  const insightMessage = useMessages().insightMessage;
  const likeMessage = useMessages().likeMessage;

  const handleMessage = useAlert().handleMessage;

  const getMessageUser = () => {
    if (message.user_id === user.id) {
      return user;
    }
    if (message.user) {
      return message.user;
    }

    return {};
  };

  const messageUser = getMessageUser();

  const messageLink = `/${messageUser.username}/m/${message.id}`;

  const disableLink = mainThread || messageId === `${message.id}`;

  const href = !disableLink ? messageLink : '';

  const threadCount = message.children?.length ?? message.children_count ?? 0;

  const like = (message.likes || []).find((lke) => lke.user_id === user.id) || {};
  const likeCount = (message.likes || []).filter((lke) => lke.marked).length;

  const insight = (message.insights || []).find((ins) => ins.user_id === user.id) || {};
  const insightCount = (message.insights || []).filter((ins) => ins.marked).length;

  const fresh = (message.freshs || []).find((fsh) => fsh.user_id === user.id) || {};
  const freshCount = (message.freshs || []).filter((fsh) => fsh.marked).length;

  const actionGroupClassName = classNames('action-group', {
    'main-action-group': mainThread,
  });
  const threadContainerClassName = classNames('thread', {
    'hover:bg-white focus:bg-white': disableLink,
  });
  const likeContainerClassName = classNames('like', {
    liked: like?.marked,
  });
  const insightContainerClassName = classNames('insight', {
    insighted: insight?.marked,
  });
  const freshContainerClassName = classNames('fresh', {
    freshed: fresh?.marked,
  });

  const handleLike = () => {
    if (!user.id) {
      return {
        data: null,
        action: () => Promise.resolve(() => login()),
      };
    }
    return {
      data: like,
      action: () => likeMessage({ message, user_id: user.id, like }),
    };
  };

  const handleInsight = () => {
    if (!user.id) {
      return {
        data: null,
        action: () => Promise.resolve(() => login()),
      };
    }
    return {
      data: insight,
      action: () => insightMessage({ message, user_id: user.id, insight }),
    };
  };

  const handleFresh = () => {
    if (!user.id) {
      return {
        data: null,
        action: () => Promise.resolve(() => login()),
      };
    }
    return {
      data: fresh,
      action: () => freshMessage({ message, user_id: user.id, fresh }),
    };
  };

  const handleShare = async () => {
    const shareLink = `${window.location.origin}${messageLink}`;
    if (navigator.share !== undefined) {
      await navigator.share({ url: shareLink });
    } else {
      navigator.clipboard.writeText(shareLink);
      handleMessage({
        message: {
          message: 'Message link copied!',
          type: 'success',
        },
      });
    }
  };

  return (
    <section variant="clear" className={actionGroupClassName}>
      <MessageAction
        className={threadContainerClassName}
        Icon={AiOutlineMessage}
        count={threadCount}
        href={href}
        label="Message thread"
      />
      <MessageAction
        onClick={handleLike}
        className={likeContainerClassName}
        Icon={like?.marked ? AiFillHeart : AiOutlineHeart}
        count={!mainThread && likeCount}
        label="Like this comment"
      />
      <MessageAction
        onClick={handleInsight}
        className={insightContainerClassName}
        Icon={insight?.marked ? IoBookSharp : IoBookOutline}
        count={!mainThread && insightCount}
        label="Mark this comment as insightful"
      />
      <MessageAction
        onClick={handleFresh}
        className={freshContainerClassName}
        Icon={fresh?.marked ? AiFillBulb : AiOutlineBulb}
        count={!mainThread && freshCount}
        label="Mark this comment as fresh"
      />
      <MessageAction
        className="share"
        onClick={handleShare}
        Icon={isMobile ? FiShare : FiLink2}
        label="Share this comment"
      />
    </section>
  );
}
